/**
 * Layout: Theme
 * -----------------------------------------------------------------------------
 * Entry file for all global theme scripts and registering sections and modules.
 *
 */
import '@/styles/theme.scss';
import { focusHash, bindInPageLinks } from '@shopify/theme-a11y';
import { load } from '@shopify/theme-sections';
import axios from 'axios';
/**
 * Global theme helpers.
 */

import { keyboardTabbable } from '~/helpers/a11y';
import AjaxCart from '~/helpers/ajax-cart';
import Currency from '~/helpers/currency';
import cssClasses from '~/helpers/cssClasses';
import EventBus from '~/helpers/event-bus';
import Redirects from '~/helpers/redirects';
import { debounce, getBrowser, on } from '~/helpers/utils';
import ScrollDirection from '~/mixins/scroll-direction';
/**
 * Global theme section imports.
 */

import '~/sections/cart-drawer';
import '~/sections/menu-drawer';
import '~/sections/shop-navigation';
import '~/sections/site-header';
import '~/sections/site-footer';
/**
 * Global theme component imports.
 */

import CookieBanner from '~/components/cookie-banner';
import FreeShippingNotification from '~/components/free-shipping-notification';
import RedirectModal from '~/components/redirect-modal';
import StoreSelector from '~/components/store-selector';
import BrontoDirectAdd from '~/components/bronto-direct-add';
import weglotSelector from '~/components/weglot-selector';
import BeaconChat from '~/components/beacon-chat';
/**
 * Global Frame utils.
 */

window.Frame = window.Frame || {};
window.Frame.EventBus = EventBus();
document.addEventListener('DOMContentLoaded', () => {
  if (getBrowser() === 'Internet Explorer') {
    axios.defaults.headers.common = {
      /**
       * IE11 caching bug.
       */
      Pragma: 'no-cache'
    };
  }
  /**
   * DOM selectors.
   */


  const selectors = {
    siteHeaderWrapper: '[js-site-header="wrapper"]',
    siteHeader: '[js-site-header="container"]'
  };
  /**
   * Instance globals.
   */

  let AnimatedHeader = {};
  /**
   * DOM node selectors.
   */

  const nodeSelectors = {
    siteHeaderWrapper: document.querySelector(selectors.siteHeaderWrapper),
    siteHeader: document.querySelector(selectors.siteHeader)
  };
  /**
   * Instantiate a collapsed header on scroll using scrollDirection helper.
   */

  function constructStickyHeader() {
    AnimatedHeader = ScrollDirection({
      throttle: 250,
      start: 100
    });
  }
  /**
   * Set sticky header events after document has loaded styling.
   */


  function setStickyHeader() {
    on('load', () => {
      AnimatedHeader.init();
      nodeSelectors.siteHeaderWrapper.classList.add(cssClasses.sticky);
      updateBodyOffset();
      on('resize', debounce(() => handleResizeEvents(), 250));
    });
  }
  /**
   * Handle resize events by requesting animation frame.
   */


  function handleResizeEvents() {
    updateBodyOffset();
  }
  /**
   * Re-paint body padding-top using current header height.
   */


  function updateBodyOffset() {
    document.body.style.paddingTop = "".concat(nodeSelectors.siteHeader.offsetHeight, "px");
  }

  load('*');

  if (theme.features.stickyHeader) {
    setStickyHeader();
    constructStickyHeader();
  }

  if (Shopify.shop !== 'meermin-seconds.myshopify.com') {
    BrontoDirectAdd().init();
  }

  AjaxCart().init();
  CookieBanner().init();
  keyboardTabbable().init();

  if (theme.features.redirectModal) {
    Redirects().init();
    RedirectModal().init();
  }

  if (theme.features.storeSelector) {
    StoreSelector().init();
  }

  if (theme.features.currencyConverter) {
    Currency().init();
  }

  if (theme.features.freeShippingNotification) {
    FreeShippingNotification().init();
  }

  focusHash();
  bindInPageLinks();
  weglotSelector().init();
  BeaconChat().init();
});